import apiClient, { getErrorMessage } from './api.service';
import { API_ENDPOINTS } from '../config/api';

export type ReceiptStatus = 'pending' | 'approved' | 'rejected';

export interface MyReceipt {
  status: ReceiptStatus;
  uploadedAt: string;
  reviewedAt?: string | null;
}

export interface AdminReceipt {
  _id: string;
  userId: { _id: string; name: string; email: string } | null;
  driveWebViewLink?: string | null;
  fileName?: string | null;
  amount?: number | null;
  status: ReceiptStatus;
  uploadedAt: string;
  reviewedAt?: string | null;
  createdAt: string;
}

export interface ReceiptPagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface AdminReceiptPage {
  receipts: AdminReceipt[];
  /** Count of approved+rejected, not-yet-cleared receipts across all pages. */
  clearableCount: number;
  pagination: ReceiptPagination;
}

class ReceiptService {
  /**
   * Upload a payment receipt file. Sends multipart/form-data so the browser
   * sets the boundary; the shared apiClient still injects the auth token.
   */
  async uploadReceipt(file: File): Promise<MyReceipt> {
    try {
      const form = new FormData();
      form.append('receipt', file);
      const response = await apiClient.post<{ status: string; data: MyReceipt }>(
        API_ENDPOINTS.RECEIPTS.UPLOAD,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /** The current user's latest receipt (null if none) — drives wizard resume. */
  async getMyReceipt(): Promise<MyReceipt | null> {
    try {
      const response = await apiClient.get<{ status: string; data: { receipt: MyReceipt | null } }>(
        API_ENDPOINTS.RECEIPTS.GET_MINE
      );
      return response.data.data.receipt;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /** Admin: list receipts (review queue), paginated. Cleared receipts are excluded. */
  async listReceipts(page = 1, limit = 10): Promise<AdminReceiptPage> {
    try {
      const response = await apiClient.get<{ status: string; data: AdminReceiptPage }>(
        API_ENDPOINTS.RECEIPTS.GET_ALL,
        { params: { page, limit } }
      );
      return response.data.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /** Admin: clear (soft-hide) a single reviewed receipt from the list. */
  async clearReceipt(id: string): Promise<void> {
    try {
      await apiClient.patch(API_ENDPOINTS.RECEIPTS.CLEAR(id));
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /** Admin: clear (soft-hide) all reviewed receipts. Returns how many were cleared. */
  async clearReviewed(): Promise<number> {
    try {
      const response = await apiClient.post<{ status: string; data: { clearedCount: number } }>(
        API_ENDPOINTS.RECEIPTS.CLEAR_REVIEWED
      );
      return response.data.data.clearedCount;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Admin: approve or reject a receipt. Approving also triggers the backend to
   * generate and email the student's access code; the returned `message`
   * reflects whether that succeeded so the UI can surface it.
   */
  async reviewReceipt(
    id: string,
    status: 'approved' | 'rejected'
  ): Promise<{ receipt: AdminReceipt; message: string }> {
    try {
      const response = await apiClient.patch<{
        status: string;
        message: string;
        data: { receipt: AdminReceipt };
      }>(API_ENDPOINTS.RECEIPTS.REVIEW(id), { status });
      return { receipt: response.data.data.receipt, message: response.data.message };
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

const receiptService = new ReceiptService();
export default receiptService;
